import type * as Basemaps from '@protomaps/basemaps'
import type { FilterSpecification, StyleSpecification } from '@maplibre/maplibre-gl-style-spec'

/**
 * Map data, fonts and sprites of the 15 km area around the town hall, served from `public/`.
 * The folder name ends in a hash of its contents: when the files change, they get a new folder (see README.md "Map data").
 */
export const MAP_ASSETS_PATH = '/maps/augsburg-194c1c30'

/** South-west and north-east corner of the area in the map data. Keep in sync with the `place` bounds in `json_schema.json`. */
export const MAP_BOUNDS: [[number, number], [number, number]] = [
  [10.696, 48.234],
  [11.101, 48.504],
]

/** OpenStreetMap places that help with orientation. Restaurants, shops and the like would compete with the block's own places. */
export const POINT_OF_INTEREST_KINDS = [
  'station',
  'bus_stop',
  'park',
  'garden',
  'forest',
  'zoo',
  'beach',
  'stadium',
  'university',
  'townhall',
  'toilets',
  'drinking_water',
  'attraction',
  'museum',
  'theatre',
  'artwork',
]

export interface MapColors {
  land: string
  water: string
  park: string
  parkMuted: string
  buildings: string
  label: string
}

/** Base map in the site's colors, with labels in the local (German) names. `origin` makes the asset URLs absolute, as MapLibre needs them. */
export function buildMapStyle(basemaps: typeof Basemaps, colors: MapColors, origin: string): StyleSpecification {
  const assetsUrl = `${origin}${MAP_ASSETS_PATH}`
  const flavor = {
    ...basemaps.namedFlavor('light'),
    background: colors.land,
    earth: colors.land,
    water: colors.water,
    park_a: colors.parkMuted,
    park_b: colors.park,
    wood_a: colors.parkMuted,
    wood_b: colors.park,
    buildings: colors.buildings,
    city_label: colors.label,
    subplace_label: colors.label,
  }
  const layers = basemaps.layers('protomaps', flavor, { lang: 'de' }).map((layer) =>
    layer.id === 'pois' && 'filter' in layer ? { ...layer, filter: pointOfInterestFilter(layer.filter!) } : layer,
  )

  return {
    version: 8,
    glyphs: `${assetsUrl}/fonts/{fontstack}/{range}.pbf`,
    sprite: `${assetsUrl}/sprites/light`,
    sources: {
      protomaps: {
        type: 'vector',
        url: `pmtiles://${assetsUrl}/tiles.pmtiles`,
        attribution: '© <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a>',
      },
    },
    layers,
  }
}

// The style filters points of interest as `["all", ["in", ["get", "kind"], ["literal", [...kinds]]], ...more conditions]`.
function pointOfInterestFilter(filter: FilterSpecification): FilterSpecification {
  const [all, , ...conditions] = filter as unknown[]
  return [all, ['in', ['get', 'kind'], ['literal', POINT_OF_INTEREST_KINDS]], ...conditions] as FilterSpecification
}

/** MapLibre understands neither CSS variables nor oklch(), so the browser resolves the color through a 1×1 canvas. */
export function resolveCssColor(element: Element, property: string): string {
  const canvas = document.createElement('canvas')
  canvas.width = 1
  canvas.height = 1
  const context = canvas.getContext('2d')!
  context.fillStyle = getComputedStyle(element).getPropertyValue(property).trim()
  context.fillRect(0, 0, 1, 1)
  const [red, green, blue] = context.getImageData(0, 0, 1, 1).data

  return `rgb(${red}, ${green}, ${blue})`
}
